/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {COMMA, ENTER} from "@angular/cdk/keycodes";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import {MatSelectionListChange} from "@angular/material/list";
import {Select, Store} from "@ngxs/store";
import {TrecAlarmData, TrecReminder} from "@trec/api/clinical-data";
import {addDays, addYears, endOfDay, startOfDay} from "date-fns";
import {Observable, Subscription} from "rxjs";
import {environment} from "../../../../../../../../environments/environment";
import {RegistryContexts} from "../../../../../../../models/constants/registry-constants";
import {CommonState} from "../../../../../../../store/common/common.state";
import {
  DEFAULT_EXAMINATIONS_LIST,
  DefaultExamination,
} from "../../../models/default-examinations";
import {
  ALARM_ANTICIPATION_UNIT,
  ALARM_TYPE,
  EXAM_PERIODIC_UNIT,
} from "../../../models/treatment-plan-enums";
import {CdItemFormComponent, CdItemFormMode} from "../cd-item-form-component";

interface FormExaminationData {
  title: string | null;
  description: string | null;

  startDate: Date | null;
  endDate: Date | null;

  frequencyType: "at_need" | "every_x_days";
  frequencyValue: number | null;
  frequencyUnit: "month" | "week" | "day";

  alarm: TrecAlarmData | null;
  subExams: string[];
}

type FormExaminationDataKeys = {[key in keyof FormExaminationData]: any};

@Component({
  selector: "c3-form-examination",
  templateUrl: "./form-examination.component.html",
  styleUrls: ["./form-examination.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormExaminationComponent
  extends CdItemFormComponent<TrecReminder[], {exams: FormExaminationData[]}>
  implements OnInit, OnDestroy {
  @Select(CommonState.patientContext) diabetesType$: Observable<
    RegistryContexts | null | undefined
  >;

  // IO ///////////////////////////////////////////////////////////////////////////////////////////

  @Input()
  public get mode(): CdItemFormMode {
    return super.mode;
  }
  public set mode(value: CdItemFormMode) {
    super.mode = value;
  }

  @Input()
  public get item(): TrecReminder[] | undefined {
    return super.item;
  }
  public set item(value: TrecReminder[] | undefined) {
    super.item = value;
  }

  @Input()
  actionPending: boolean;

  // Component elems /////////////////////////////////////////////////////////////////////////////

  @ViewChild("formElem", {read: FormGroupDirective, static: true})
  formElem: FormGroupDirective;

  // Defaults and Enums ///////////////////////////////////////////////////////////////////////////

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  readonly ALARM_ANTICIPATION_UNIT = ALARM_ANTICIPATION_UNIT;
  readonly ALARM_TYPE = ALARM_TYPE;
  readonly EXAM_PERIODIC_UNIT = EXAM_PERIODIC_UNIT;

  private readonly ALL_DEFAULT_EXAMINATIONS = DEFAULT_EXAMINATIONS_LIST;

  /**
   * Avoid error 'exam' cannot start in the past, starting from tomorrow.
   */
  readonly tomorrow = startOfDay(addDays(new Date(), 1));

  get defaultExaminationsFiltered(): DefaultExamination[] {
    if (this.diabetesType) {
      // Select only exams that have as tag the diabetes type of the patient
      let exam = this.ALL_DEFAULT_EXAMINATIONS.filter((e) =>
        e.tags.some((e1) => {
          return this.diabetesType?.includes(e1);
        })
      );
      return exam;
    } else {
      return [];
    }
  }

  // get defaultExaminationsFilteredBySearch(): DefaultExamination[] {
  //   return this.defaultExaminationsFiltered
  //     .filter(e => e.name.toLowerCase().includes(this.examSearchValue.toLowerCase())) // Filter by search
  //     .filter(e => !this.formExamsArrayValue.find(v => v.title === e.name)); // Filter already selected
  // }

  examSearchValue = "";

  readonly customExamName = new FormControl("");

  // Form fields //////////////////////////////////////////////////////////////////////////////////

  readonly form: UntypedFormGroup;

  get formExamsArrayControl(): UntypedFormArray {
    return this.form.get("exams") as UntypedFormArray;
  }

  get formExamsArrayValue(): FormExaminationDataKeys[] {
    return this.formExamsArrayControl.value;
  }

  // Others variables /////////////////////////////////////////////////////////////////////////////

  /**
   * Type of diabetes of the patient.
   */
  private diabetesType: RegistryContexts | null = null;

  private readonly subscr = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    protected cd: ChangeDetectorRef
  ) {
    super(cd);

    // Initialize form schema
    this.form = this.fb.group({
      exams: this.fb.array([]), // Exams will contain the form groups containing data of each exam
    });
  }

  ngOnInit() {
    // Subscribe for diabetes type
    this.subscr.add(
      this.diabetesType$.subscribe((diabetesType) => {
        this.diabetesType = diabetesType || null;
        this.cd.markForCheck();
      })
    );
  }

  protected resetDefaultValues() {
    // Update form accordingly to the data source
    this.form.setControl("exams", this.fb.array([]));
  }

  // Exams array handlers /////////////////////////////////////////////////////////////////////////

  toggleExamControlFromList(event: MatSelectionListChange) {
    event.options.forEach((option) => {
      const eventExam: DefaultExamination = option.value;

      if (option.selected) {
        this.addExamControl(eventExam);
      } else {
        const i = this.formExamsArrayValue.findIndex((e) => e.title === eventExam.name);
        this.removeExamControl(i);
      }
    });
  }

  addExamControl(data: TrecReminder | DefaultExamination) {
    if (!data) {
      return;
    }

    let examGroup: UntypedFormGroup | undefined;

    if (data instanceof TrecReminder) {
      examGroup = this._createExamControlTrecReminder(data);
    } else if (typeof data === "string") {
      // Invalid input
    } else {
      examGroup = this._createExamControlDefaultExamination(data);
    }

    if (examGroup) {
      // Subscribe for startDate form
      const startDateControl = examGroup.get("startDate") as UntypedFormControl;
      this.subscr.add(
        startDateControl.valueChanges.subscribe((startDate) => {
          // Change end date accordingly
          const endDateControl = examGroup && (examGroup.get("endDate") as UntypedFormControl);
          if (endDateControl && endDateControl.value <= startDate) {
            endDateControl.patchValue(addYears(startDate ?? new Date(), 5));
          }
        })
      );

      // Add to array
      this.formExamsArrayControl.push(examGroup);
      // Set form as dirty
      this.form.markAsDirty();

      // Clear search
      this.examSearchValue = "";
    }
  }

  removeExamControl(index: number) {
    this.formExamsArrayControl.removeAt(index);

    // TODO unsubscribe also from formChanges (x2)
  }

  private _createExamControlTrecReminder(exam: TrecReminder): UntypedFormGroup {
    const data = exam.data;
    const metadata = exam.metadata;

    return this.fb.group({
      title: [data.title, [Validators.required, Validators.maxLength(200)]],
      description: [data.description, Validators.maxLength(10000)],
      startDate: [new Date(metadata.start_date), Validators.required], // Date
      endDate: [new Date(metadata.end_date), Validators.required], // Date

      frequencyType: [data.regular.frequency.type, Validators.required],
      frequencyUnit: ["day", Validators.required],
      frequencyValue: [
        data.regular.frequency.values ? data.regular.frequency.values[0] : null,
        [Validators.required, Validators.min(1)],
      ],

      alarm: this.fb.group({
        type: [data.alarm.type, Validators.required],
        anticipation_value: [data.alarm.anticipation_value, Validators.required],
        anticipation_unit: [data.alarm.anticipation_unit, Validators.required],
      }),
      subExams: this.fb.control(data.tags || []),
    } as FormExaminationDataKeys);
  }

  private _createExamControlDefaultExamination(data: DefaultExamination): UntypedFormGroup {
    return this.fb.group({
      title: [data.name, [Validators.required, Validators.maxLength(200)]],
      description: ["", Validators.maxLength(10000)],
      startDate: [this.tomorrow, Validators.required], // Date
      endDate: [addYears(this.tomorrow, 5), Validators.required], // Date

      frequencyType: [data.frequency ? "every_x_days" : "at_need", Validators.required],
      frequencyUnit: [data.frequency ? data.frequency.unit : "month", Validators.required],
      frequencyValue: [
        data.frequency ? data.frequency.value : 1,
        [Validators.required, Validators.min(1)],
      ],

      alarm: this.fb.group({
        type: ["notification", Validators.required],
        anticipation_value: [1, Validators.required],
        anticipation_unit: ["week", Validators.required],
      }),
      subExams: this.fb.control(data.subExams || []),
    } as FormExaminationDataKeys);
  }

  onAddCustomExam() {
    if (this.mode !== "insert") {
      return;
    }
    if (!this.customExamName.value) {
      return;
    }

    this.addExamControl({name: this.customExamName.value, tags: []});
    this.customExamName.patchValue("");
  }

  // Callbacks ////////////////////////////////////////////////////////////////////////////////////

  prepareData(): TrecReminder[] | null {
    // Convert form into exam
    const isEditMode = this.mode === "edit";

    if (isEditMode && !this.item) {
      throw new Error(`Trying to submit editing of a exam but exam data are unknown`);
    }

    // Create all exams
    const exams: TrecReminder[] = (
      this.formExamsArrayControl.value as FormExaminationDataKeys[]
    ).map((values) => this.createTrecReminder(values, isEditMode ? this.item?.[0] : undefined));

    return exams;
  }

  update() {
    super.update();

    this.resetDefaultValues();

    // Remove all exams from array
    while (this.formExamsArrayControl.length) {
      this.formExamsArrayControl.removeAt(0);
    }

    // Update form
    (this.item ?? []).forEach((v) => {
      this.addExamControl(v);
    });

    // Enable/disable executions (this does not happen when form is enabled/disabled)
    if (this.isViewMode) {
      this.formExamsArrayControl.disable();
    } else {
      this.formExamsArrayControl.enable();
    }
  }

  findDefaultExamination(name: string): DefaultExamination | undefined {
    return this.defaultExaminationsFiltered.find((e) => e.name === name);
  }

  isExaminationSelected(name: string): boolean {
    return !!this.formExamsArrayValue.find((e) => e.title === name);
  }

  // Utils ////////////////////////////////////////////////////////////////////////////////////////

  private createTrecReminder(examGroup: FormExaminationDataKeys, oldTrecReminder?: TrecReminder) {
    return TrecReminder.create(
      {
        id: oldTrecReminder ? oldTrecReminder.id : undefined,
        patientId: oldTrecReminder
          ? oldTrecReminder.patientId || ""
          : this.store.selectSnapshot(CommonState.patientId) || "",
      },
      {
        start_date: startOfDay(examGroup.startDate).valueOf(),
        end_date: this.normalizedEndDate(examGroup.frequencyType, examGroup.endDate).valueOf(),
        source: oldTrecReminder ? oldTrecReminder.metadata.source : environment.appId,
        provenance: oldTrecReminder ? oldTrecReminder.metadata.provenance : "medical",
        device: oldTrecReminder ? oldTrecReminder.metadata.device : undefined,
        doctor: {
          id: this.store.selectSnapshot(CommonState.userId)!,
          name:
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.surname! +
            " " +
            this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.name,
        },
      },
      {
        title: examGroup.title,
        description: examGroup.description,
        alarm: examGroup.alarm,
        regular: {
          executions: [],
          frequency: {
            type: examGroup.frequencyType,
            values:
              examGroup.frequencyType !== "at_need"
                ? [this.normalizedFrequencyValue(examGroup.frequencyUnit, examGroup.frequencyValue)]
                : [],
          },
        },
        variable: {
          executions: [],
        },
        tags: examGroup.subExams || [],
      }
    );
  }

  private normalizedEndDate(frequencyType: string, endDate?: Date): Date {
    const freqFlag = frequencyType === "every_x_days";
    if (freqFlag) {
      if (!endDate) {
        throw new Error(
          `Bad exam's endDate. endDate is missing but frequency has been selected (${frequencyType})`
        );
      }
      return endOfDay(endDate);
    } else {
      // Return "endDate"
      if (!endDate) {
        throw new Error(`Error with endDate`);
      }
      return new Date(endDate);
    }
  }

  private normalizedFrequencyValue(frequencyUnit?: string, frequencyValue?: number): number {
    switch (frequencyUnit) {
      case "year":
        return (frequencyValue || 0) * 365;
      case "month":
        return (frequencyValue || 0) * 30;
      case "week":
        return (frequencyValue || 0) * 7;
      case "day":
        return frequencyValue || 0;
      default:
        throw new Error(`Unknown exam frequencyUnit "${frequencyUnit}"`);
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.subscr.unsubscribe();
  }
}
